/**
 * Typed client for the Drawing Analyzer API.
 *
 * Extracted values are defined by the prompts rather than by code, so anything
 * carrying them is typed as an open record. Only the envelope - ids, counts,
 * timestamps - has a fixed shape, which mirrors how the backend models it.
 *
 * No credentials are sent. This build serves one fixed library to whoever opens
 * it, so there is no session to attach and no token to refresh. Nothing here
 * writes: there is no upload call and no delete call, because the library is a
 * fixed set of prepared drawings that the app only ever reads.
 */
import { filenameFromDisposition } from "./download";
import type { DownloadedFile } from "./download";

/** A value as returned by the model: scalar, list, or a nested object. */
export type FieldValue = string | number | boolean | null | FieldValue[] | { [key: string]: FieldValue };

/** Label/value pairs whose keys come from the prompt, not from this file. */
export type Fields = Record<string, FieldValue>;

/** Normalised [x0, y0, x1, y1] in the 0..1 range, relative to the page. */
export type BBox = [number, number, number, number];

/**
 * One detected region: a single part of one of the sheet's drawings.
 *
 * The `group_*` keys say which drawing that is. A sheet interleaves its views,
 * so one rectangle per drawing cannot be both tight and free of its neighbour's
 * content - the extraction therefore reads tight part boxes and records which
 * drawing each belongs to, and the viewer draws both levels.
 *
 * All of it is optional: a record analyzed before the grouping existed carries
 * none of it, and then the viewer simply draws the one flat level it used to.
 */
export interface Position {
  fields?: Fields;
  drawing?: Fields | null;
  source_page?: number;
  /** Normalised [x0, y0, x1, y1] in the 0..1 range, if the model located it. */
  bbox?: BBox | null;
  /** 1-based index of the drawing this part belongs to, in detection order. */
  group?: number;
  /** The drawing's own name where the sheet gave it one: "SECTION A-A". */
  group_label?: string;
  /** view | section | detail | isometric | plan | elevation | table | notes | title_block | other */
  group_kind?: string;
  /** The drawing's own box - the union of its parts. Drawn, never read from. */
  group_bbox?: BBox | null;
  /** What this part is within its drawing, as the locate pass named it. */
  label?: string;
}

/** What one analysis produced. */
export interface ExtractionResult {
  header?: Fields;
  header_source_page?: number | null;
  positions?: Position[];
}

/** A library entry as shown in the grid. */
export interface DocumentSummary {
  id: string;
  name: string;
  created: string;
  n_positions: number;
  /** How many of the sheet's drawings those regions belong to; 0 if ungrouped. */
  n_drawings: number;
  n_fields: number;
  thumb: string | null;
  score: number | null;
}

/** A library entry with everything the detail screen needs. */
export interface DocumentDetail extends DocumentSummary {
  data: ExtractionResult;
  pages: string[];
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatSource {
  id: string;
  name: string;
  score: number;
}

/** What an answer was drawn from. */
export type ChatScope = "library" | "pinned";

export interface ChatResponse {
  answer: string;
  sources: ChatSource[];
  /**
   * Echoed by the server rather than assumed by the client: the pin can be
   * changed while an answer is in flight, and an old answer must not be
   * relabelled by a scope it was never asked under.
   */
  scope: ChatScope;
}

export interface CompareResponse {
  answer: string;
  documents: DocumentSummary[];
}

export interface Health {
  ok: boolean;
  detail: string;
  documents: number;
  /** Where the documents are kept, for the status bar's tooltip. */
  library: string;
}

/** An API call that failed, carrying the backend's own explanation. */
export class ApiError extends Error {
  readonly status: number;
  readonly kind: string;

  constructor(status: number, detail: string, kind = "error") {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.kind = kind;
  }
}

const BASE = "/api";

/**
 * Turn a failed response into an ApiError carrying the server's message.
 *
 * The backend explains what to fix (missing key, unreadable library, broken
 * prompt), so that text is preferred over anything invented here. Non-JSON
 * bodies still produce a usable message rather than a parse crash.
 */
async function toError(response: Response): Promise<ApiError> {
  let detail = `${response.status} ${response.statusText}`;
  let kind = "error";
  try {
    const body = await response.json();
    if (typeof body?.detail === "string") detail = body.detail;
    if (typeof body?.kind === "string") kind = body.kind;
  } catch {
    // A proxy error or a crash can return HTML; the status line is enough.
  }
  return new ApiError(response.status, detail, kind);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, init);
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * The same call as `request`, for a route that answers with a file.
 *
 * Failures still go through `toError`, so a 404 or an unreachable library reads
 * as the server's own sentence rather than as a downloaded file full of it.
 *
 * @param fallback The name to save the file as if the server did not name it.
 */
async function requestFile(
  path: string,
  fallback: string,
  init?: RequestInit,
): Promise<DownloadedFile> {
  const response = await fetch(`${BASE}${path}`, init);
  if (!response.ok) throw await toError(response);
  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get("Content-Disposition")) ?? fallback,
  };
}

export const api = {
  health: () => request<Health>("/health"),

  listDocuments: () => request<DocumentSummary[]>("/documents"),

  getDocument: (id: string) => request<DocumentDetail>(`/documents/${id}`),

  /**
   * Download one document's extraction as CSV, for processing elsewhere.
   *
   * A GET, because it is a document turned into a file and nothing changes.
   */
  exportDocumentCsv: (id: string) =>
    requestFile(`/documents/${id}/export.csv`, "drawing-export.csv"),

  /**
   * Download a whole selection as one CSV file.
   *
   * The ids are sent as a body, matching the bulk delete, and their order is the
   * order the rows appear in - so the file reads the way the grid was ticked.
   */
  exportDocumentsCsv: (ids: string[]) =>
    requestFile("/documents/export.csv", "drawing-export.csv", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ document_ids: ids }),
    }),

  search: (query: string, k = 24) =>
    request<DocumentSummary[]>("/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, k }),
    }),

  /**
   * Ask the assistant a question.
   *
   * `documentIds` pins the answer: given, those documents are the whole of the
   * context and no other may reach the answer. Omitted, the assistant searches
   * the library. Which it is, is the user's choice and is shown to them above
   * the composer, so it is sent explicitly rather than inferred from the screen.
   */
  chat: (question: string, history: ChatMessage[], documentIds?: string[]) =>
    request<ChatResponse>("/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history,
        document_ids: documentIds && documentIds.length > 0 ? documentIds : null,
      }),
    }),

  /**
   * Ask about a chosen set of documents.
   *
   * The ids are sent rather than a query, so the answer is about exactly these
   * documents. An empty question asks for a general comparison.
   */
  compareDocuments: (documentIds: string[], question: string) =>
    request<CompareResponse>("/compare", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ document_ids: documentIds, question }),
    }),
};
